import {
  normalizeReleaseSha,
  type MapsCallSource,
  type MapsClientEvent,
} from "~/lib/maps/attribution";
import { logMapsClientEvent } from "~/lib/server-functions/log-maps-client-event";

/**
 * Privacy-safe browser telemetry for the Dynamic Maps surface.
 *
 * Only a closed enum of event names plus the callsite and release are sent — no
 * URLs, query strings, coordinates, or user identifiers.
 *
 * These are diagnostics, not billing records: Google Cloud is authoritative for
 * Maps JavaScript API / Dynamic Map usage.
 */

/** Events that must fire at most once per page load. */
const ONCE_PER_PAGE: ReadonlySet<MapsClientEvent> = new Set([
  "map_mounted",
  "maps_js_load_ok",
]);

const alreadyReported = new Set<string>();

function releaseSha(): string | undefined {
  return (
    normalizeReleaseSha(import.meta.env.VITE_APP_RELEASE_SHA) ?? undefined
  );
}

export function reportMapsClientEvent(
  event: MapsClientEvent,
  source: MapsCallSource,
): void {
  if (typeof window === "undefined") return;

  if (ONCE_PER_PAGE.has(event)) {
    const key = `${event}|${source}`;
    if (alreadyReported.has(key)) return;
    alreadyReported.add(key);
  }

  void logMapsClientEvent({
    data: { event, source, releaseSha: releaseSha() },
  }).catch(() => {
    // Telemetry must never surface to the user or break the map.
  });
}

/** Test seam. */
export function __resetMapsClientTelemetryForTests(): void {
  alreadyReported.clear();
}
